import { gql } from '@apollo/client';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@compiled/react';
import {
	createPlacement,
	type FrontendStage,
	type PlacementMessageBackendFactory,
	type PlacementMessageFrontendFactory,
} from '@post-office/placement-common-v2';
import {
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultPlacementBackend,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';
import { placementMiddlewareWithCacheControlNav } from '@post-office/placement-common-v2/middlewares/custom-headers';
import { defaultBackendPlacementStages } from '@post-office/placement-common-v2/placement-stages';
import {
	type BackendMessageBody,
	type BackendPlacementStageData,
	type BackendPlacementStageInput,
	type CustomRenderer,
	type DefaultMessagesResponse,
} from '@post-office/placement-common-v2/types';
import type { SerializableRecord } from '@post-office/serializable-record';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const styles = cssMap({
	root: {
		color: token('color.text.subtlest'),
		font: token('font.heading.xxsmall'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.075'),
	},
});

export type PlacementRenderProps = { dismiss: () => void };

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse | undefined;
};

const placement = createPlacement<PlacementDefinition>({
	placementId: 'nav-4-projects-section',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
});
export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;
export const createBackend: (
	placementBackendParams?:
		| DefaultPlacementBackend<{
				transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
				hydrate?: AnyBackendMessageStage;
		  }>
		| undefined,
) => PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = placement.createBackend;

const getRecommendationsQuery = gql`
	query Nav4RecommendationsQuery(
		$cloudId: ID!
		$rerankCandidateList: [GrowthRecRerankCandidate!]!
	) {
		jira {
			canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId) @optIn(to: "JiraAction")
		}
		growthRecommendations {
			recommendations(
				context: {
					useCase: "jiraProjectMenuRecommendations"
					tenantId: $cloudId
					product: "jira"
					custom: { version: "v3" }
				}
				first: 1
				rerank: $rerankCandidateList
			) {
				... on GrowthRecRecommendations {
					data {
						... on GrowthRecJiraTemplateRecommendation {
							entityId
						}
					}
					context {
						sessionId
					}
				}
			}
		}
	}
`;

interface RecommendationsQueryResponse {
	jira?: {
		canCreateProject?: boolean;
	};
	growthRecommendations?: {
		recommendations?: {
			data?: ReadonlyArray<{
				entityId?: string;
			}>;
			context?: {
				sessionId?: string;
			};
		};
	};
}

// Turn off sorting based on https://atlassian.slack.com/archives/C05SKG8KUAG/p1754448904308239?thread_ts=1754448426.227599&cid=C05SKG8KUAG
export const PlacementBackend: PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = createBackend({
	customMiddleware: placementMiddlewareWithCacheControlNav,
	params: {
		sortingMethod: undefined,
		dataRetrivalType: 'implicit-only',
	},
	stages: ({ hydrate, transformMetadata }) => ({
		init: defaultBackendPlacementStages({ hydrate, transformMetadata }).init,
		postInit: async (
			input: BackendPlacementStageInput<BackendMessageBody<{ entityId: string }>>,
		) => {
			const { data, request } = input;
			// Hydrate the original messages
			const { messages }: BackendPlacementStageData<{ entityId: string }> =
				(await hydrate?.(input)) ?? data;

			const nav4ProjectsRecommendationsMessages = messages.filter(
				(message) => message.messageTemplateId === 'nav-4-projects-section-recommendation',
			);

			request.logger.info(
				{ nav4ProjectsRecommendationsMessages },
				'Nav-4 projects recommendations messages received for postInit stage',
			);

			// If there is no recommendation message, we don't need to fetch recommendations
			if (nav4ProjectsRecommendationsMessages.length === 0) {
				request.logger.info(
					{ nav4ProjectsRecommendationsMessages },
					'No message available for nav-4-projects-section-recommendation',
				);
				return {
					messages,
				};
			}

			const enableXForwardedHostHeader = request.statsig.checkGate(
				{
					identifiers: {
						atlassianAccountId: request.orchestrationContext.recipient.userId,
					},
				},
				'post-office_enable_x-forwarded-host-header',
			);

			// Use the implicit messages from nav-4-projects-section-recommendation as the rerank list
			// Filter out messages with null/undefined entityId to avoid GraphQL validation errors
			const rerankCandidateList = nav4ProjectsRecommendationsMessages
				.filter((message) => message.context?.entityId)
				.map((message) => ({
					entityId: message.context.entityId,
				}));

			if (rerankCandidateList.length === 0) {
				request.logger.info(
					{ rerankCandidateList },
					'No rerank candidate list found for nav-4-projects-section-recommendation',
				);
				return {
					messages: messages.filter(
						(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
					),
				};
			}

			let queryResult: Awaited<
				ReturnType<
					typeof request.graphqlClients.cachedGraphqlGateway.query<RecommendationsQueryResponse>
				>
			>;

			try {
				queryResult =
					await request.graphqlClients.cachedGraphqlGateway.query<RecommendationsQueryResponse>(
						{
							query: getRecommendationsQuery,
							variables: {
								cloudId: request.orchestrationContext.tenantId,
								rerankCandidateList,
							},
							context: {
								headers: {
									...(enableXForwardedHostHeader && request.headers?.['x-forwarded-host']
										? { 'x-forwarded-host': request.headers['x-forwarded-host'] as string }
										: {}),
								},
							},
						},
						{},
					);
			} catch (error) {
				// Do not throw on 403 or other GraphQL/network errors; treat as no recommendations
				request.logger.error(
					{ error },
					'GraphQL request failed for nav-4-projects-section-recommendation, skipping recommendations',
				);
				return {
					messages: messages.filter(
						(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
					),
				};
			}

			const { data: recommendation, errors, error } = queryResult;

			if (error || errors?.length) {
				request.logger.error(
					{
						error,
						errors,
						recommendation,
					},
					'Error fetching recommendations for nav-4-projects-section-recommendation',
				);
				// Ignore nav-4-projects-section-recommendation if anything wrong with the GRS call
				return {
					messages: messages.filter(
						(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
					),
				};
			}

			if (!recommendation.jira?.canCreateProject) {
				request.logger.info(
					{
						recommendation,
					},
					'User does not have permission to create project for nav-4-projects-section-recommendation',
				);
				// Ignore nav-4-projects-section-recommendation if no recommendation returned from the GRS call
				return {
					messages: messages.filter(
						(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
					),
				};
			}

			if (!recommendation.growthRecommendations?.recommendations?.context?.sessionId) {
				request.logger.info(
					{
						recommendation,
					},
					'No sessionId returned for nav-4-projects-section-recommendation',
				);
				// Ignore nav-4-projects-section-recommendation if no recommendation returned from the GRS call
				return {
					messages: messages.filter(
						(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
					),
				};
			}

			if (!recommendation.growthRecommendations?.recommendations?.data?.length) {
				request.logger.info(
					{ nav4ProjectsRecommendationsMessages },
					'No recommendations found for nav-4-projects-section-recommendation, use the first message in the original list as a fallback',
				);
				return {
					messages: [
						...messages.filter(
							(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
						),
						nav4ProjectsRecommendationsMessages[0],
					] as BackendMessageBody<{
						entityId: string;
					}>[],
				};
			}

			const recommendedMessages = nav4ProjectsRecommendationsMessages
				// We only want the recommended template to be returned
				.filter((message) =>
					recommendation.growthRecommendations?.recommendations?.data?.some(
						(r) => r.entityId === message.context.entityId,
					),
				)
				.map((message) => ({
					...message,
					context: {
						...message.context,
						// Add recommendationSessionId returned from GRS into the message context which is passed into CFFE.
						recommendationSessionId:
							recommendation.growthRecommendations?.recommendations?.context?.sessionId ?? '',
					},
				}));

			request.logger.info(
				{ recommendedMessages },
				'Recommended messages for nav-4-projects-section-recommendation',
			);
			return {
				messages: [
					...messages.filter(
						(message) => message.messageTemplateId !== 'nav-4-projects-section-recommendation',
					),
					...recommendedMessages,
				],
			};
		},
	}),
});

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => ({ preloadedInitialData }) => {
	const [{ messages, status }, setMessagesState] = useState<{
		messages: BackendMessageBody<SerializableRecord>[] | undefined;
		status: 'loading' | 'done';
	}>(() => {
		if (preloadedInitialData) {
			return preloadedInitialData?.messages?.length > 0
				? {
						status: 'done',
						messages: preloadedInitialData?.messages,
					}
				: { messages: undefined, status: 'done' };
		}

		return { messages: undefined, status: 'loading' };
	});

	const isDone = status === 'done';

	const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
		autoRefetch: {
			isEnabled: !isDone,
			updateKey: 'nav-4-projects-section',
		},
	});

	const handleDismiss = useCallback(
		(messageInstanceId: string): void => {
			setMessagesState(() => {
				if (!messages) {
					return { messages: undefined, status: 'done' };
				}

				const filteredMessages = messages.filter(
					(message) => messageInstanceId !== message.messageInstanceId,
				);

				return {
					messages: filteredMessages,
					status: 'done',
				};
			});
		},
		[messages],
	);

	useEffect(() => {
		if (!isLoading && !isDone) {
			setMessagesState({
				messages: data?.messages,
				status: 'done',
			});
		}
	}, [isLoading, isDone, data]);

	if (error) {
		return null;
	}

	if (status === 'loading') {
		return null;
	}

	return Array.isArray(messages) && messages.length > 0 ? (
		<div role="list">
			<div role="group" aria-labelledby="nav-4-projects-section-menu-section">
				<h2 css={styles.root} id="nav-4-projects-section-menu-section-heading">
					<FormattedMessage
						id="post-office.nav-4-projects-recommendations.section-heading"
						defaultMessage="Recommended"
						description="heading for nav4 projects recommendations section"
					/>
				</h2>
				<div role="listitem">
					{messages?.map((props, index) => (
						<Messages.render
							key={`${props.messageInstanceId}-${index}`}
							dismiss={() => handleDismiss(props.messageInstanceId)}
							{...props}
						/>
					))}
				</div>
			</div>
		</div>
	) : null;
});
